import { Circle, Text } from 'react-konva';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import annTools from './commonAnnotationTools';
import Annotation from './annotation';
const fontFamily = 'Helvetica';
const baseFontSize = 18;
const minFontSize = 12;
const labelLineHeight = 1.15;

// Extract pure functions to improve testability
export const calculateDefaultFontSize = scale => baseFontSize * scale;

/**
 * Spaces become line breaks in the pin (matches iPad word-wrap; Konva wrap is unreliable here).
 * Applies to all Note/issue pins (fab form annotations and legacy report issues).
 */
export const formatAnnotationLabelForDisplay = label => {
  if (!label) {
    return '';
  }
  const normalizedLabel = String(label).trim().replace(/\s+/g, ' ');
  if (!normalizedLabel) {
    return '';
  }
  return normalizedLabel.replace(/ /g, '\n');
};

/** Widest line after display formatting — not the full single-line width. */
export const measureWidestLineSegment = (context, label, fontSizePx, family) => {
  if (!context || !label) {
    return 0;
  }
  const displayLabel = formatAnnotationLabelForDisplay(label);
  context.font = `${fontSizePx}px ${family}`;
  const lines = displayLabel.split(/\r?\n/).filter(line => line.length > 0);
  if (lines.length === 0) {
    return 0;
  }
  return Math.max(...lines.map(line => context.measureText(line).width));
};

/**
 * Largest font (px) so every display line fits in the circle's 80% text box (width and height).
 * Never returns below minFontSize (scaled down when defaultFontSize is smaller).
 */
export const determineFontSize = (context, label, defaultFontSize, boxWidth, family = fontFamily, constraintPercentage = 0.8) => {
  const effectiveMinFontSize = Math.min(minFontSize, defaultFontSize);
  if (!label) {
    return defaultFontSize;
  }
  const displayLabel = formatAnnotationLabelForDisplay(label);
  const lines = displayLabel.split(/\r?\n/).filter(line => line.length > 0);
  const lineCount = Math.max(lines.length, 1);
  const maxWidth = boxWidth * constraintPercentage;
  const maxHeight = boxWidth * constraintPercentage;
  const heightCap = Math.floor(maxHeight / (lineCount * labelLineHeight));
  const startSize = Math.min(defaultFontSize, heightCap);
  const fits = size => {
    if (size < effectiveMinFontSize) {
      return false;
    }
    if (size * lineCount * labelLineHeight > maxHeight) {
      return false;
    }
    if (context && measureWidestLineSegment(context, label, size, family) > maxWidth) {
      return false;
    }
    return true;
  };
  for (let candidate = startSize; candidate >= effectiveMinFontSize; candidate -= 1) {
    if (fits(candidate)) {
      return candidate;
    }
  }
  return effectiveMinFontSize;
};
const Note = props => {
  const {
    draggable,
    scale,
    pageHeight,
    offset,
    dpiScale,
    selectedID,
    annotation,
    onDragEnd
  } = props;
  const [adjustedFontSize, setAdjustedFontSize] = useState(null);
  const textNodeRef = useRef(null);
  const prevScaleRef = useRef(scale);
  const prevLabelRef = useRef('');

  // Support both noteAnnotation (new) and issue (legacy)
  const noteData = annotation.noteAnnotation || annotation.issue;
  const {
    content: rawContent
  } = noteData || {};
  const {
    id
  } = annotation;
  let content = {};
  if (rawContent) {
    try {
      content = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
    } catch (_unused) {
      content = {};
    }
  }

  /**
   * @function updateFontSize
   * set the fontSize of a Note's label to be as wide as possible (80% of container width)
   */
  const updateFontSize = useCallback(() => {
    if (!textNodeRef.current || !content.label) {
      return;
    }
    const defaultFontSize = calculateDefaultFontSize(scale);
    const width = textNodeRef.current.getAttr('width');
    const stage = textNodeRef.current.getStage();
    let context = null;
    try {
      if (stage !== null && stage !== void 0 && stage.container) {
        const container = stage.container();
        const canvas = container ? container.querySelector('canvas') : null;
        context = canvas ? canvas.getContext('2d') : null;
      }
    } catch (_unused2) {
      // skip measurement
    }
    if (!context) {
      return;
    }
    const newFontSize = determineFontSize(context, content.label, defaultFontSize, width, fontFamily);
    if (newFontSize !== adjustedFontSize) {
      setAdjustedFontSize(newFontSize);
    }
  }, [scale, content.label, adjustedFontSize]);

  // Update font size when scale or content changes
  useEffect(() => {
    if (scale !== prevScaleRef.current || content.label !== prevLabelRef.current) {
      updateFontSize();
      prevScaleRef.current = scale;
      prevLabelRef.current = content.label;
    }
  }, [scale, content.label, updateFontSize]);

  // Make sure the label scales on initial load instead of truncating.
  useLayoutEffect(() => {
    // Force synchronous update after the text node is rendered
    if (textNodeRef.current && content.label) updateFontSize();
  }, [content.label, updateFontSize]);

  // only care about the first one
  const [box] = annTools.adaptPDFRects(scale, pageHeight, annotation, offset);
  const color = annTools.parseColor(content.color);
  const colorRGBAString = `rgba(${color[0]},${color[1]},${color[2]},0.6)`;
  // After migrating csv jsons to rgba, we can remove the fixed alpha value from the color string
  // const colorRGBAString = `rgba(${color[0]},${color[1]},${color[2]},${color[3]})`;
  const textColorValue = annotation.textColor ? annTools.parseColor(annotation.textColor) : null;
  const textColor = textColorValue ? `rgba(${textColorValue[0]},${textColorValue[1]},${textColorValue[2]},0.8)` : 'rgba(255,255,255,0.8)';

  // Base dimensions that will be scaled
  const baseCircleDiameter = 30;
  const baseSelectionBoxSize = 30;

  // Scale the dimensions based on the zoom factor and DPI scale
  const circleDiameter = baseCircleDiameter * scale / dpiScale;
  const selectionBoxSize = baseSelectionBoxSize * scale / dpiScale;

  // Calculate offsets to center the circle within the selection box
  const circleOffset = (selectionBoxSize - circleDiameter) / 2;
  const adjustedX = box.x / dpiScale + circleOffset;
  const adjustedY = box.y / dpiScale + circleOffset;

  // Center the circle within the selection box
  const circleAdjustedX = adjustedX + circleDiameter / 2;
  const circleAdjustedY = adjustedY + circleDiameter / 2;
  const circleRadius = 0.5 * circleDiameter;
  const displayLabel = formatAnnotationLabelForDisplay(content.label);
  return /*#__PURE__*/React.createElement(Annotation, {
    draggable: draggable,
    box: {
      x: box.x,
      y: box.y,
      w: selectionBoxSize,
      h: selectionBoxSize
    },
    selectedID: selectedID,
    annotationID: id,
    dpiScale: dpiScale,
    scale: scale,
    onDragEnd: onDragEnd
  }, /*#__PURE__*/React.createElement(Circle, {
    key: `${id}-circle`,
    fill: colorRGBAString,
    x: circleAdjustedX,
    y: circleAdjustedY,
    radius: circleRadius
  }), /*#__PURE__*/React.createElement(Text, {
    key: `${id}-text`,
    x: adjustedX,
    y: adjustedY,
    width: circleDiameter,
    height: circleDiameter,
    fontFamily: fontFamily,
    fontSize: adjustedFontSize || calculateDefaultFontSize(scale),
    fill: textColor,
    align: "center",
    verticalAlign: "middle",
    lineHeight: labelLineHeight,
    text: displayLabel,
    wrap: "none",
    ref: textNodeRef
  }));
};
Note.propTypes = {
  annotation: PropTypes.shape({
    colorString: PropTypes.string,
    id: PropTypes.string,
    noteAnnotation: PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
        color: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
        label: PropTypes.string,
        prevRawContent: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
      })])
    }),
    issue: PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
        color: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
        label: PropTypes.string,
        prevRawContent: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
      })])
    }),
    textColor: PropTypes.string
  }).isRequired,
  dpiScale: PropTypes.number.isRequired,
  draggable: PropTypes.bool.isRequired,
  offset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }).isRequired,
  onDragEnd: PropTypes.func.isRequired,
  pageHeight: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  selectedID: PropTypes.string.isRequired
};
export default Note;